import {ArrowDownRight, ArrowUpRight} from 'lucide-react'
import { useEffect, useState } from 'react'
import { formatNumber } from '@/lib/format'
import { weiToEthNumber } from '@/lib/launchpad/wei'
import { listTradeActivity, onTradeActivityRecorded, type TradeActivityRecord, type TradeKind } from '@/lib/tradeActivity'

function shortenWallet(address: string): string {
  if (!address || address.length < 10)
    return address || 'Unknown wallet'
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

function timeAgo(iso: string): string {
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms))
    return ''
  const diff = Date.now() - ms
  if (diff < 60_000)
    return 'just now'
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60)
    return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24)
    return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function safeBigInt(value: string | undefined): bigint {
  try {
    return BigInt(value || '0')
  }
  catch {
    return 0n
  }
}

interface RecentTradesProps {
  projectKey?: string
  kind: TradeKind
  accent?: 'lime' | 'orange'
}

function RecentTrades({ projectKey, kind, accent = 'lime' }: RecentTradesProps) {
  const [trades, setTrades] = useState<TradeActivityRecord[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!projectKey)
      return
    let active = true
    listTradeActivity(projectKey, kind, 15).then((rows) => {
      if (active) {
        setTrades(rows)
        setLoaded(true)
      }
    })
    const unsubscribe = onTradeActivityRecorded(() => {
      listTradeActivity(projectKey, kind, 15).then((rows) => {
        if (active)
          setTrades(rows)
      })
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [projectKey, kind])

  if (!projectKey)
    return null

  const accentText = accent === 'lime' ? 'text-lime-400' : 'text-orange-400'

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
      <h2 className="text-sm font-semibold text-white">Recent buys &amp; sells</h2>
      {!loaded
        ? <p className="mt-3 text-xs text-zinc-500">Loading recent activity…</p>
        : trades.length === 0
          ? <p className="mt-3 text-xs text-zinc-500">No trades yet. Be the first to buy.</p>
          : (
              <ul className="mt-3 space-y-2.5">
                {trades.map((trade) => {
                  const amountWei = kind === 'presale' ? trade.quoteAmount : trade.tokenAmount
                  const unitLabel = kind === 'presale' ? 'ETH' : 'tokens'
                  return (
                    <li key={trade.id} className="flex items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-2 text-zinc-400">
                        {trade.side === 'buy'
                          ? <ArrowUpRight className={`h-3.5 w-3.5 ${accentText}`} aria-hidden="true" />
                          : <ArrowDownRight className="h-3.5 w-3.5 text-red-400" aria-hidden="true" />}
                        <span className="font-medium text-white">{shortenWallet(trade.wallet)}</span>
                        <span>{trade.side === 'buy' ? 'bought' : 'sold'}</span>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${trade.side === 'buy' ? accentText : 'text-red-400'}`}>
                          {formatNumber(weiToEthNumber(safeBigInt(amountWei)))}
                          {' '}
                          {unitLabel}
                        </p>
                        <p className="text-[10px] text-zinc-500">{timeAgo(trade.createdAt)}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
    </div>
  )
}

export default RecentTrades
